// A small QR code maker (byte mode, error correction level M, versions 1–6),
// enough for member codes and short links. Returns a square grid of dark/light modules.

const EC_M: Record<number, { ec: number; blocks: number[] }> = {
  // version: codewords of error correction per block, and data codewords in each block
  1: { ec: 10, blocks: [16] },
  2: { ec: 16, blocks: [28] },
  3: { ec: 26, blocks: [44] },
  4: { ec: 18, blocks: [32, 32] },
  5: { ec: 24, blocks: [43, 43] },
  6: { ec: 16, blocks: [27, 27, 27, 27] },
};
const ALIGN: Record<number, number[]> = { 1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30], 6: [6, 34] };

// ---- Reed–Solomon over GF(256), polynomial 0x11D
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
})();
const mul = (a: number, b: number) => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

function generator(degree: number): number[] {
  let g = [1];
  for (let i = 0; i < degree; i++) {
    const next = new Array(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) {
      next[j] ^= g[j];
      next[j + 1] ^= mul(g[j], EXP[i]);
    }
    g = next;
  }
  return g;
}

function ecCodewords(data: number[], degree: number): number[] {
  const gen = generator(degree);
  const res = new Array(degree).fill(0);
  for (const d of data) {
    const factor = d ^ res.shift()!;
    res.push(0);
    for (let i = 0; i < degree; i++) res[i] ^= mul(gen[i + 1], factor);
  }
  return res;
}

// ---- matrix
type Grid = { size: number; dark: boolean[][]; fixed: boolean[][] };

function setFixed(g: Grid, r: number, c: number, dark: boolean) {
  g.dark[r][c] = dark;
  g.fixed[r][c] = true;
}

function placeFinder(g: Grid, r0: number, c0: number) {
  for (let r = -1; r <= 7; r++) {
    for (let c = -1; c <= 7; c++) {
      const rr = r0 + r;
      const cc = c0 + c;
      if (rr < 0 || cc < 0 || rr >= g.size || cc >= g.size) continue;
      const inRing = r >= 0 && r <= 6 && c >= 0 && c <= 6 && (r === 0 || r === 6 || c === 0 || c === 6);
      const inCore = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      setFixed(g, rr, cc, inRing || inCore);
    }
  }
}

function formatBits(mask: number): number {
  const data = (0b00 << 3) | mask; // level M = 00
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >> 9) * 0x537);
  return ((data << 10) | (rem & 0x3ff)) ^ 0x5412;
}

function placeFormat(g: Grid, mask: number) {
  const bits = formatBits(mask);
  const bit = (i: number) => ((bits >> i) & 1) === 1;
  // Around the top-left finder.
  for (let i = 0; i <= 5; i++) setFixed(g, i, 8, bit(i));
  setFixed(g, 7, 8, bit(6));
  setFixed(g, 8, 8, bit(7));
  setFixed(g, 8, 7, bit(8));
  for (let i = 9; i < 15; i++) setFixed(g, 8, 14 - i, bit(i));
  // The copy split between the other two finders.
  for (let i = 0; i < 8; i++) setFixed(g, 8, g.size - 1 - i, bit(i));
  for (let i = 8; i < 15; i++) setFixed(g, g.size - 15 + i, 8, bit(i));
  setFixed(g, g.size - 8, 8, true); // the dark module
}

function baseGrid(version: number): Grid {
  const size = version * 4 + 17;
  const g: Grid = { size, dark: Array.from({ length: size }, () => new Array(size).fill(false)), fixed: Array.from({ length: size }, () => new Array(size).fill(false)) };
  placeFinder(g, 0, 0);
  placeFinder(g, 0, size - 7);
  placeFinder(g, size - 7, 0);
  for (let i = 8; i < size - 8; i++) {
    setFixed(g, 6, i, i % 2 === 0);
    setFixed(g, i, 6, i % 2 === 0);
  }
  const pos = ALIGN[version];
  for (const r of pos) {
    for (const c of pos) {
      if ((r === 6 && c === 6) || (r === 6 && c === pos[pos.length - 1]) || (r === pos[pos.length - 1] && c === 6)) continue;
      for (let dr = -2; dr <= 2; dr++) for (let dc = -2; dc <= 2; dc++) setFixed(g, r + dr, c + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1);
    }
  }
  placeFormat(g, 0); // reserve the format areas; real bits are written after masking
  return g;
}

const MASKS: ((r: number, c: number) => boolean)[] = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

function placeData(g: Grid, bits: number[]) {
  let i = 0;
  let upward = true;
  for (let right = g.size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let k = 0; k < g.size; k++) {
      const r = upward ? g.size - 1 - k : k;
      for (let dc = 0; dc < 2; dc++) {
        const c = right - dc;
        if (g.fixed[r][c]) continue;
        g.dark[r][c] = i < bits.length ? bits[i] === 1 : false;
        i++;
      }
    }
    upward = !upward;
  }
}

function penalty(d: boolean[][]): number {
  const n = d.length;
  let score = 0;
  // Runs of the same color in rows and columns.
  for (let pass = 0; pass < 2; pass++) {
    for (let a = 0; a < n; a++) {
      let run = 1;
      for (let b = 1; b <= n; b++) {
        const same = b < n && (pass ? d[b][a] === d[b - 1][a] : d[a][b] === d[a][b - 1]);
        if (same) run++;
        else {
          if (run >= 5) score += 3 + (run - 5);
          run = 1;
        }
      }
    }
  }
  // 2x2 blocks.
  for (let r = 0; r < n - 1; r++) for (let c = 0; c < n - 1; c++) if (d[r][c] === d[r][c + 1] && d[r][c] === d[r + 1][c] && d[r][c] === d[r + 1][c + 1]) score += 3;
  // Finder-like patterns.
  const pat = [true, false, true, true, true, false, true];
  for (let r = 0; r < n; r++) {
    for (let c = 0; c + 7 <= n; c++) {
      for (const horizontal of [true, false]) {
        const at = (k: number) => (horizontal ? d[r][c + k] : d[c + k][r]);
        if (!pat.every((p, k) => at(k) === p)) continue;
        const light = (from: number, to: number) => {
          for (let k = from; k < to; k++) {
            const v = horizontal ? d[r]?.[c + k] : d[c + k]?.[r];
            if (v === true) return false;
          }
          return true;
        };
        if ((c - 4 >= 0 && light(-4, 0)) || (c + 11 <= n && light(7, 11))) score += 40;
      }
    }
  }
  // Balance of dark and light.
  let darkCount = 0;
  for (const row of d) for (const v of row) if (v) darkCount++;
  score += Math.floor(Math.abs((darkCount * 100) / (n * n) - 50) / 5) * 10;
  return score;
}

/** Encodes text as a QR code. Returns rows of booleans (true = dark), without the quiet zone. */
export function qrMatrix(text: string): boolean[][] {
  const bytes = [...new TextEncoder().encode(text)];
  const version = [1, 2, 3, 4, 5, 6].find((v) => EC_M[v].blocks.reduce((s, b) => s + b, 0) * 8 >= 4 + 8 + bytes.length * 8);
  if (!version) throw new Error('Text is too long for this QR code.');
  const { ec, blocks } = EC_M[version];
  const capacity = blocks.reduce((s, b) => s + b, 0);
  // Mode (byte), length, data, terminator and padding.
  const bits: number[] = [];
  const push = (v: number, len: number) => { for (let i = len - 1; i >= 0; i--) bits.push((v >> i) & 1); };
  push(0b0100, 4);
  push(bytes.length, 8);
  for (const b of bytes) push(b, 8);
  push(0, Math.min(4, capacity * 8 - bits.length));
  while (bits.length % 8) bits.push(0);
  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) data.push(parseInt(bits.slice(i, i + 8).join(''), 2));
  for (let pad = 0xec; data.length < capacity; pad = pad === 0xec ? 0x11 : 0xec) data.push(pad);
  // Split into blocks, add error correction, interleave.
  const dataBlocks: number[][] = [];
  let offset = 0;
  for (const len of blocks) {
    dataBlocks.push(data.slice(offset, offset + len));
    offset += len;
  }
  const ecBlocks = dataBlocks.map((b) => ecCodewords(b, ec));
  const codewords: number[] = [];
  const maxLen = Math.max(...blocks);
  for (let i = 0; i < maxLen; i++) for (const b of dataBlocks) if (i < b.length) codewords.push(b[i]);
  for (let i = 0; i < ec; i++) for (const b of ecBlocks) codewords.push(b[i]);
  const allBits: number[] = [];
  for (const cw of codewords) for (let i = 7; i >= 0; i--) allBits.push((cw >> i) & 1);
  // Try each mask and keep the easiest to scan.
  let best: { score: number; dark: boolean[][] } | null = null;
  for (let m = 0; m < 8; m++) {
    const g = baseGrid(version);
    placeData(g, allBits);
    for (let r = 0; r < g.size; r++) for (let c = 0; c < g.size; c++) if (!g.fixed[r][c] && MASKS[m](r, c)) g.dark[r][c] = !g.dark[r][c];
    placeFormat(g, m);
    const score = penalty(g.dark);
    if (!best || score < best.score) best = { score, dark: g.dark };
  }
  return best!.dark;
}

/** An SVG path that draws the dark modules, with a 4-module quiet zone. */
export function qrSvgPath(text: string): { size: number; path: string } {
  const m = qrMatrix(text);
  const q = 4;
  let path = '';
  m.forEach((row, r) => row.forEach((dark, c) => { if (dark) path += `M${c + q} ${r + q}h1v1h-1z`; }));
  return { size: m.length + q * 2, path };
}
